import type { CSSProperties } from 'react';
import { fontStyleToCss } from '@/lib/fontStyle';
import type { DepartureHeading } from '@/store/transforms';
import {
  DEPARTURE_FONT_SIZE,
  LEGACY_BORDER_COLOR,
  type DepartureFonts,
} from '@/widgets/DepartureWidget/options';

/**
 * One direction of one line: the heading, then its departure times. Port of
 * `departure/departureTimes.vue`.
 *
 * Purely presentational — its whole input is already-shaped data plus the font blocks. The
 * legacy component's only logic was the two copy-pasted font computeds that
 * `fontStyleToCss` replaces.
 */

export type DepartureTimesProps = {
  heading: DepartureHeading;
  fonts: DepartureFonts;
};

/**
 * The legacy `.headingLabel` rule, minus the font half. A fixed 220px is what keeps a long
 * direction name from widening the column instead of being clipped, and it is in pixels in
 * the legacy stylesheet rather than rem — preserved, because on a 1920px display the two
 * differ by a factor of 1.375.
 */
const HEADING_CLAMP: CSSProperties = {
  width: '220px',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  borderColor: LEGACY_BORDER_COLOR,
};

export function DepartureTimes({ heading, fonts }: DepartureTimesProps) {
  const headingStyle = fontStyleToCss(fonts.heading, { fontSize: DEPARTURE_FONT_SIZE });
  const timeStyle = fontStyleToCss(fonts.times, { fontSize: DEPARTURE_FONT_SIZE });

  return (
    <div className="flex flex-col w-1/2">
      <p className="border-b-1 headingLabel px-2" style={{ ...headingStyle, ...HEADING_CLAMP }}>
        {heading.heading}
      </p>
      {heading.times.map((time, index) => (
        <p key={index} className="timeLabel" style={timeStyle}>
          {time}
        </p>
      ))}
    </div>
  );
}
